import React from 'react';
import PropTypes from 'prop-types';

/**
 * 
 * @param {string} priority - اولویت اقدام (فوری_و_اضطراری، بسیار_مهم، مهم، کم_اهمیت، غیرضروری)
 * @param {string} className - کلاس‌های اضافی برای استایل
 * @param {boolean} showIcon - نمایش آیکون در کنار متن
 * @param {string} size - اندازه badge (sm, md, lg)
 */
const PriorityBadge = ({ priority, className = '', showIcon = true, size = 'md' }) => {
  if (!priority) return null;

  const getPriorityConfig = (priorityValue) => {
    const priorityStr = String(priorityValue).toLowerCase();
    
    if (priorityStr.includes('فوری') || priorityStr.includes('urgent') || priorityStr === 'فوری_و_اضطراری') {
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-700 dark:text-red-400',
        icon: '▲',
        iconColor: 'text-red-600 dark:text-red-400',
        border: 'border-red-200 dark:border-red-800',
      };
    }
    
    if (priorityStr.includes('بسیار') || priorityStr.includes('very') || priorityStr === 'بسیار_مهم') {
      return {
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        text: 'text-orange-700 dark:text-orange-400',
        icon: '▲',
        iconColor: 'text-orange-600 dark:text-orange-400',
        border: 'border-orange-200 dark:border-orange-800',
      };
    }
    
    if (priorityStr === 'مهم' || priorityStr === 'important' || priorityStr === 'high') {
      return {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        text: 'text-amber-700 dark:text-amber-400',
        icon: '■',
        iconColor: 'text-amber-600 dark:text-amber-400',
        border: 'border-amber-200 dark:border-amber-800',
      };
    }
    
    if (priorityStr.includes('کم') || priorityStr.includes('low') || priorityStr === 'کم_اهمیت') {
      return {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        text: 'text-emerald-700 dark:text-emerald-400',
        icon: '▼',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
        border: 'border-emerald-200 dark:border-emerald-800',
      };
    }
    
    if (priorityStr.includes('غیر') || priorityStr.includes('unnecessary') || priorityStr === 'غیرضروری') {
      return {
        bg: 'bg-gray-50 dark:bg-gray-800/50',
        text: 'text-gray-600 dark:text-gray-400',
        icon: '○',
        iconColor: 'text-gray-500 dark:text-gray-400',
        border: 'border-gray-200 dark:border-gray-700',
      };
    }
    
    // پیش‌فرض
    return {
      bg: 'bg-gray-50 dark:bg-gray-800/50',
      text: 'text-gray-600 dark:text-gray-400',
      icon: '●',
      iconColor: 'text-gray-500 dark:text-gray-400',
      border: 'border-gray-200 dark:border-gray-700',
    };
  };

  // تعریف اندازه‌ها
  const sizeClasses = {
    sm: {
      container: 'px-2 py-0.5 text-xs gap-1',
      icon: 'text-[9px]',
    },
    md: {
      container: 'px-2.5 py-1 text-xs gap-1',
      icon: 'text-[10px]',
    },
    lg: {
      container: 'px-3 py-1.5 text-sm gap-1.5',
      icon: 'text-xs',
    },
  };

  const config = getPriorityConfig(priority);
  const sizeConfig = sizeClasses[size] || sizeClasses.md;

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full
        ${config.bg} ${config.text} ${config.border}
        border ${sizeConfig.container}
        transition-colors duration-200
        ${className}
      `}
      style={{ fontFamily: 'CustomFont' }}
    >
      {showIcon && (
        <span className={`${config.iconColor} flex-shrink-0 ${sizeConfig.icon} leading-none`}>
          {config.icon}
        </span>
      )}
      <span className="whitespace-nowrap">{priority}</span>
    </span>
  );
};

PriorityBadge.propTypes = {
  priority: PropTypes.string,
  className: PropTypes.string,
  showIcon: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export default PriorityBadge;
